import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New user form
  const [newUser, setNewUser] = useState({ name: '', phone: '', role: 'player' });
  // New location form
  const [newLocation, setNewLocation] = useState('');
  // New adjustment form
  const [newAdj, setNewAdj] = useState({ userId: '', amount: '', reason: '' });

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    loadAll();
  }, []);

  const [allRatings, setAllRatings] = useState([]);
  const [editRating, setEditRating] = useState(null); // { userId, errors, defense, offense }

  const loadAll = async () => {
    setLoading(true);
    try {
      const [u, l, a, r] = await Promise.all([
        API.get('/admin/users'),
        API.get('/admin/locations'),
        API.get('/admin/adjustments'),
        API.get('/ratings/all'),
      ]);
      setUsers(u.data);
      setLocations(l.data);
      setAdjustments(a.data);
      setAllRatings(r.data);
    } catch (err) { setError('Failed to load data'); }
    finally { setLoading(false); }
  };

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const addUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await API.post('/admin/users', newUser);
      setNewUser({ name: '', phone: '', role: 'player' });
      flash('User added! They can now set their PIN on first login.');
      loadAll();
    } catch (err) { setError(err.response?.data?.error || 'Error adding user'); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Remove ${name} from Pedal Toast?`)) return;
    try { await API.delete(`/admin/users/${id}`); loadAll(); flash('User removed.'); }
    catch (err) { setError(err.response?.data?.error || 'Error removing user'); }
  };

  const resetPin = async (id, name) => {
    if (!window.confirm(`Reset PIN for ${name}? They'll set a new one on next login.`)) return;
    try { await API.patch(`/admin/users/${id}/reset-pin`); flash(`PIN reset for ${name}.`); }
    catch { setError('Error resetting PIN'); }
  };

  const addLocation = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await API.post('/admin/locations', { name: newLocation });
      setNewLocation('');
      flash('Location added.');
      loadAll();
    } catch { setError('Error adding location'); }
  };

  const deleteLocation = async (id, name) => {
    if (!window.confirm(`Remove location "${name}"?`)) return;
    try { await API.delete(`/admin/locations/${id}`); loadAll(); flash('Location removed.'); }
    catch { setError('Error removing location'); }
  };

  const addAdjustment = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await API.post('/admin/adjustments', { userId: newAdj.userId, amount: parseFloat(newAdj.amount), reason: newAdj.reason });
      setNewAdj({ userId: '', amount: '', reason: '' });
      flash('Adjustment saved.');
      loadAll();
    } catch { setError('Error saving adjustment'); }
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="topbar-title">Admin Panel</div>
        <span style={{ fontSize: 11, background: 'rgba(124,106,247,0.2)', color: 'var(--accent2)', padding: '4px 10px', borderRadius: 50, fontWeight: 700 }}>ADMIN</span>
      </div>
      <div className="page fade-in">
        {error && <div className="error-msg">{error}</div>}
        {success && <div style={{ background: 'rgba(90,247,160,0.1)', border: '1px solid rgba(90,247,160,0.3)', color: 'var(--success)', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14 }}>{success}</div>}

        <div className="tabs">
          {['users', 'locations', 'adjustments', 'ratings'].map(t => (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => { setTab(t); setError(''); }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {loading ? <div className="loading">Loading...</div> : (
          <>
            {/* USERS TAB */}
            {tab === 'users' && (
              <div>
                <div className="card" style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Add New Player</div>
                  <form onSubmit={addUser}>
                    <div className="form-group">
                      <label className="form-label">Name</label>
                      <input className="form-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone (with country code)</label>
                      <input className="form-input" placeholder="+923001234567" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select className="form-input" value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                        <option value="player">Player</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <button type="submit" className="btn btn-primary">Add Player</button>
                  </form>
                </div>

                <div className="section-header"><div className="section-title">All Players ({users.length})</div></div>
                {users.map(u => (
                  <div key={u.id} className="card" style={{ padding: '14px 18px' }}>
                    <div className="row-between">
                      <div className="flex gap-8">
                        <div className="avatar">{u.name[0]}</div>
                        <div>
                          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {u.name}
                            {u.role === 'admin' && <span style={{ fontSize: 10, background: 'rgba(124,106,247,0.2)', color: 'var(--accent2)', padding: '2px 8px', borderRadius: 50, fontWeight: 700 }}>ADMIN</span>}
                            {u.first_login && <span style={{ fontSize: 10, background: 'rgba(247,192,90,0.2)', color: 'var(--warning)', padding: '2px 8px', borderRadius: 50, fontWeight: 700 }}>PIN NOT SET</span>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{u.phone}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--warning)', fontSize: 12 }} onClick={() => resetPin(u.id, u.name)}>Reset PIN</button>
                        {u.id !== user.id && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', fontSize: 12 }} onClick={() => deleteUser(u.id, u.name)}>Remove</button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* LOCATIONS TAB */}
            {tab === 'locations' && (
              <div>
                <div className="card" style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Add Court Location</div>
                  <form onSubmit={addLocation}>
                    <div className="form-group">
                      <label className="form-label">Location Name</label>
                      <input className="form-input" placeholder="e.g. DHA Court 3" value={newLocation} onChange={e => setNewLocation(e.target.value)} required />
                    </div>
                    <button type="submit" className="btn btn-primary">Add Location</button>
                  </form>
                </div>

                <div className="section-header"><div className="section-title">Locations ({locations.length})</div></div>
                {locations.map(l => (
                  <div key={l.id} className="card" style={{ padding: '14px 18px' }}>
                    <div className="row-between">
                      <span style={{ fontWeight: 600 }}>📍 {l.name}</span>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => deleteLocation(l.id, l.name)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ADJUSTMENTS TAB */}
            {tab === 'adjustments' && (
              <div>
                <div className="card" style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Manual Adjustment</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Use positive amounts to credit a user, negative to debit.</div>
                  <form onSubmit={addAdjustment}>
                    <div className="form-group">
                      <label className="form-label">Player</label>
                      <select className="form-input" value={newAdj.userId} onChange={e => setNewAdj({ ...newAdj, userId: e.target.value })} required>
                        <option value="">Select player...</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Amount (Rs.) — use negative to deduct</label>
                      <input type="number" className="form-input" placeholder="e.g. 500 or -200" value={newAdj.amount} onChange={e => setNewAdj({ ...newAdj, amount: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Reason</label>
                      <input className="form-input" placeholder="e.g. Correcting session #12 error" value={newAdj.reason} onChange={e => setNewAdj({ ...newAdj, reason: e.target.value })} required />
                    </div>
                    <button type="submit" className="btn btn-primary">Save Adjustment</button>
                  </form>
                </div>

                <div className="section-header"><div className="section-title">Adjustment History</div></div>
                {adjustments.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text2)', padding: '32px', fontSize: 14 }}>No adjustments yet.</div>
                ) : adjustments.map(a => (
                  <div key={a.id} className="card" style={{ padding: '14px 18px' }}>
                    <div className="row-between" style={{ marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{a.user_name}</span>
                      <span style={{ fontFamily: 'Syne', fontWeight: 700, color: parseFloat(a.amount) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {parseFloat(a.amount) >= 0 ? '+' : ''}Rs. {parseFloat(a.amount).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{a.reason}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>by {a.admin_name} · {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
            {/* RATINGS TAB */}
            {tab === 'ratings' && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                  View all players' self-rated performance averages. Set starting ratings for new players.
                </div>
                {allRatings.map(r => (
                  <div key={r.id} className="card" style={{ padding: '14px 18px' }}>
                    <div className="row-between" style={{ marginBottom: 10 }}>
                      <div className="flex gap-8">
                        {r.photo_url ? (
                          <img src={r.photo_url} alt={r.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div className="avatar">{r.name[0]}</div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{r.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{r.total_rated_sessions} rated sessions</div>
                        </div>
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent)', fontSize: 12 }}
                        onClick={() => setEditRating({ userId: r.id, name: r.name, errors: r.rating_errors || 3, defense: r.rating_defense || 3, offense: r.rating_offense || 3 })}>
                        Set Starting
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text2)' }}>Errors: </span><strong>{r.rating_errors ? parseFloat(r.rating_errors).toFixed(1) : '—'}</strong></div>
                      <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text2)' }}>Defense: </span><strong>{r.rating_defense ? parseFloat(r.rating_defense).toFixed(1) : '—'}</strong></div>
                      <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text2)' }}>Offense: </span><strong>{r.rating_offense ? parseFloat(r.rating_offense).toFixed(1) : '—'}</strong></div>
                    </div>
                  </div>
                ))}

                {/* Edit starting rating modal */}
                {editRating && (
                  <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditRating(null)}>
                    <div className="modal">
                      <div className="modal-handle" />
                      <div style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Set Starting Ratings</div>
                      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>{editRating.name}</div>
                      {['errors', 'defense', 'offense'].map(field => (
                        <div key={field} className="form-group">
                          <label className="form-label">{field.charAt(0).toUpperCase() + field.slice(1)} (1–5)</label>
                          <input type="number" min="1" max="5" step="0.1" className="form-input"
                            value={editRating[field]}
                            onChange={e => setEditRating({ ...editRating, [field]: e.target.value })} />
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEditRating(null)}>Cancel</button>
                        <button className="btn btn-primary" style={{ flex: 2 }} onClick={async () => {
                          try {
                            await API.patch(`/ratings/admin/${editRating.userId}`, {
                              ratingErrors: editRating.errors,
                              ratingDefense: editRating.defense,
                              ratingOffense: editRating.offense,
                            });
                            setEditRating(null);
                            flash('Starting ratings saved.');
                            loadAll();
                          } catch { setError('Error saving ratings'); }
                        }}>Save</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
